<script setup lang="ts">
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
	Button,
} from '@laam/ui/base';
import { PhCaretRight } from '@phosphor-icons/vue';
import SizeTableComponent from './SizeTableComponent.vue';

import HowToMeasureFigure from './HowToMeasure.vue';
import CustomShirtForm from './CustomShirtForm.vue';
import CustomTrouserForm from './CustomTrouserForm.vue';
import { useFetchSizeChart } from '~/data/product';
import { productInjectKey, type Product } from '~/types/Product';

const isOpen = ref(false);

const productData = inject<Product>(productInjectKey);

const sizeChartId = computed(() =>
	productData ? productData.size_chart_id : 0,
);

const result = await useFetchSizeChart(sizeChartId.value, isOpen);

const sizeChartData = computed(() => {
	if (result && result.status.value === 'success') {
		return result.data.value;
	} else {
		return {};
	}
});
</script>

<template class="pdp__size-chart">
	<Drawer v-model:open="isOpen" class="huh drawer">
		<DrawerTrigger
			class="flex items-center text-blue-700 pdp__size-chart-trigger gap-xs"
		>
			<span class="text-sm font-semibold text-blue-700"> Size Guide </span>
			<PhCaretRight class="size-3xl" />
		</DrawerTrigger>

		<DrawerContent class="h-[90%]">
			<DrawerHeader>
				<DrawerTitle class="text-lg font-semibold text-gray-800">
					Size Guide
				</DrawerTitle>
			</DrawerHeader>

			<Tabs default-value="size-chart" class="size-chart-tabs">
				<TabsList
					class="grid grid-cols-2 bg-white border-b border-gray-200 border-solid rounded-none py-none"
				>
					<TabsTrigger
						value="size-chart"
						class="mx-xl size-chart-tab data-[state=active]:text-gray-700 data-[state=active]:border-gray-700 border-solid text-gray-500 pb-xl rounded-none border-0 shadow-transparent border-b-2 px-xs p-xs border-white"
					>
						Size Chart
					</TabsTrigger>
					<TabsTrigger
						value="guide"
						class="size-chart-tab mx-xl data-[state=active]:text-gray-700 data-[state=active]:border-gray-700 border-solid text-gray-500 pb-xl rounded-none border-0 shadow-transparent border-b-2 px-xs p-xs border-white"
					>
						How to measure
					</TabsTrigger>
				</TabsList>

				<TabsContent value="size-chart" class="pb-7xl h-[72vh] overflow-auto">
					<div v-if="showCustomSizeChart" class="size-chart-form">
						<div class="">
							<form id="customShirtForm" @submit="onSubmit">
								<CustomShirtForm :custom-shirt="customShirt" />
								<div class="border-b border-gray-600" />
								<CustomTrouserForm :custom-trouser="customTrouser" />
							</form>
						</div>
						<div class="">
							<Button variant="secondary" @click="resetForm">
								<span class="font-medium text-gray-800 text-md">Reset</span>
							</Button>
							<Button variant="primary" form="customShirtForm">
								<span class="font-medium text-white text-md">Save</span>
							</Button>
						</div>
					</div>

					<div v-else class="size-chart-tables">
						<div
							v-for="(sizeChart, label, index) in sizeChartData"
							:key="index"
						>
							<SizeTableComponent :size-chart="sizeChart" :title="label" />
						</div>
					</div>
				</TabsContent>
				<TabsContent value="guide">
					<div class="px-3xl py-8xl">
						<HowToMeasureFigure class="w-full pt-lg" />
					</div>
				</TabsContent>
			</Tabs>
		</DrawerContent>
	</Drawer>
</template>
